import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Drawflow from 'drawflow';
import 'drawflow/dist/drawflow.min.css';
import {
    MdArrowBack, MdSave, MdFormatColorText, MdCropSquare, MdCircle, MdZoomIn, MdZoomOut
} from 'react-icons/md';
import {
    FaHome, FaWater, FaFan, FaBuilding, FaThermometerHalf, FaNetworkWired, FaTools, FaCircle,
    FaTint, FaFilter, FaIndustry, FaLeaf, FaFlask, FaExclamationTriangle,
    FaBolt, FaDatabase, FaServer, FaCogs, FaWrench, FaGasPump
} from 'react-icons/fa';
import {
    GiWaterTower, GiValve, GiPipes, GiOilDrum, GiFactory,
    GiWaterfall, GiPressureCooker, GiElectric, GiRadarSweep, GiWaterSplash,
    GiMechanicalArm, GiWaterDrop, GiWaterRecycling, GiChemicalTank, GiChemicalArrow,
    GiChemicalBolt, GiVial, GiWaterMill, GiPipeOrgan, GiTank,
    GiLightningStorm, GiGears, GiWrench, GiSpanner, GiPowerGenerator,
    GiOilPump, GiBlender, GiMolecule, GiAcid
} from 'react-icons/gi';
import {
    TbDroplet, TbDropletHalf2, TbWaveSine, TbTemperature, TbGauge,
    TbEngine, TbPropeller, TbAirConditioning, TbBuildingFactory2,
    TbChartLine, TbAlertTriangle, TbPlugConnected, TbBattery2, TbSunElectricity
} from 'react-icons/tb';
import { renderToString } from 'react-dom/server';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useLanguage } from '../../context/LanguageContext';

export default function ScadaBuilder() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t } = useLanguage();
    const [company, setCompany] = useState(null);
    const [plcCards, setPlcCards] = useState([]);
    const [loading, setLoading] = useState(true);
    const editorRef = useRef(null);
    const drawflowInstance = useRef(null);

    const applyResizes = useCallback((editor) => {
        const layoutData = editor.export();
        const nodes = layoutData.drawflow.Home.data;
        // Ensure DOM is updated before updating connection points
        setTimeout(() => {
            for (let nodeId in nodes) {
                const node = nodes[nodeId];
                const el = document.getElementById(`node-${nodeId}`);
                if (el && node.data.width) {
                    const resizable = el.querySelector('.resizable-shape');
                    if (resizable) {
                        resizable.style.width = `${node.data.width}px`;
                        resizable.style.height = `${node.data.height}px`;
                    }
                }
                // Important: refresh connection lines for this node
                try {
                    editor.updateConnectionNodes('node-' + nodeId);
                } catch(e) {}
            }
        }, 100);
    }, []);

    const initDrawflow = useCallback((comp) => {
        if (!editorRef.current) return;
        if (drawflowInstance.current) {
            drawflowInstance.current.clear();
        } else {
            const editor = new Drawflow(editorRef.current);
            editor.reroute = true;
            editor.editor_mode = 'edit';
            editor.start();
            drawflowInstance.current = editor;

            // Events
            editor.on('nodeCreated', (id) => {
                console.log("Node created " + id);
            });
        }

        if (comp.scadaLayout && comp.scadaLayout.drawflow) {
            try {
                drawflowInstance.current.import(comp.scadaLayout);
                applyResizes(drawflowInstance.current);
            } catch (err) {
                console.error("SCADA import hatası:", err);
            }
        }
    }, [applyResizes]);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const [compRes, countRes, sigRes, plcRes] = await Promise.all([
                api.get(`/companies/${id}`),
                api.get(`/counters/company/${id}`),
                api.get(`/status-signals/company/${id}`),
                api.get(`/plc/company/${id}`)
            ]);

            const companyData = compRes.data.data;
            const countersData = countRes.data.data || [];
            const signalsData = sigRes.data.data || [];
            setPlcCards(plcRes.data.data || []);
            
            // Map counters to systems
            if (companyData.systems) {
                companyData.systems = companyData.systems.map(s => ({
                    ...s,
                    counters: countersData.filter(c => {
                        const sid = c.systemId?._id || c.systemId;
                        return sid && sid.toString() === s._id.toString();
                    })
                }));
            }
            
            // Attach status signals to company object
            companyData.statusSignals = signalsData;
            
            setCompany(companyData);

            // Allow React to drop the loading screen and render the canvas DOM element before initializing Drawflow
            setTimeout(() => {
                initDrawflow(companyData);
            }, 100);
        } catch (err) {
            toast.error(t('scada.fetchError'));
        } finally {
            setLoading(false);
        }
    }, [id, initDrawflow, t]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const saveLayout = async () => {
        if (!drawflowInstance.current) return;

        // Before export, capture manually resized elements and textareas
        const exported = drawflowInstance.current.export();
        const nodesData = exported.drawflow.Home.data;

        document.querySelectorAll('.drawflow-node').forEach(el => {
            const nodeId = el.id.replace('node-', '');
            const resizable = el.querySelector('.resizable-shape');
            if (resizable && nodesData[nodeId]) {
                const w = resizable.offsetWidth;
                const h = resizable.offsetHeight;
                nodesData[nodeId].data.width = w;
                nodesData[nodeId].data.height = h;
            }

            // capture textarea inputs explicitly
            const ta = el.querySelector('textarea');
            if (ta && nodesData[nodeId]) {
                nodesData[nodeId].data.text = ta.value;
            }
        });

        try {
            await api.put(`/companies/${id}/scada`, { scadaLayout: JSON.stringify(exported) });
            toast.success(t('scada.saveSuccess'));
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.message || err.message || t('scada.saveError'));
        }
    };

    const onDragStart = (e, type, payload) => {
        e.dataTransfer.setData('type', type);
        e.dataTransfer.setData('payload', JSON.stringify(payload));
    };

    const onDrop = (e) => {
        e.preventDefault();
        const type = e.dataTransfer.getData('type');
        const payloadStr = e.dataTransfer.getData('payload');
        if (!type) return;

        const payload = JSON.parse(payloadStr);

        // Adjust for editor center coordinates (drawflow transform)
        const parentRect = editorRef.current.getBoundingClientRect();
        const x = e.clientX - parentRect.left;
        const y = e.clientY - parentRect.top;
        const dfZoom = drawflowInstance.current.zoom;
        const dfPosX = drawflowInstance.current.canvas_x;
        const dfPosY = drawflowInstance.current.canvas_y;

        const finalX = x * (drawflowInstance.current.precanvas.clientWidth / (drawflowInstance.current.precanvas.clientWidth * dfZoom)) - (dfPosX / dfZoom);
        const finalY = y * (drawflowInstance.current.precanvas.clientHeight / (drawflowInstance.current.precanvas.clientHeight * dfZoom)) - (dfPosY / dfZoom);

        let html = '';
        let nodeClass = '';
        let inputs = 1;
        let outputs = 1;
        let data = {};

        if (type === 'system') {
            nodeClass = 'scada-system';
            const sensorsHtml = (payload.sensors || []).map(s => `
                <div class="scada-sensor-item">
                    <span class="sensor-name">${s.sensorName}</span>
                    <span class="sensor-val" data-sensor-uuid="${s.sensorUUID}">--</span>
                    <span class="sensor-unit">${s.unit}</span>
                </div>
            `).join('');

            const countersHtml = (payload.counters || []).map(c => `
                <div class="scada-sensor-item scada-counter-item">
                    <span class="sensor-name">${c.counterName}</span>
                    <span class="sensor-val" data-counter-uuid="${c.counterUUID}">--</span>
                    <span class="sensor-unit">${c.unit}</span>
                </div>
            `).join('');

            html = `
                <div class="scada-node-card">
                    <div class="scada-node-header">
                        <i class="fa fa-server"></i> ${payload.systemName}
                    </div>
                    <div class="scada-node-body">
                        ${sensorsHtml}
                        ${countersHtml}
                        ${(!payload.sensors?.length && !payload.counters?.length) ? `<span class="text-muted">${t('scada.noDevices')}</span>` : ''}
                    </div>
                </div>
            `;
            data = { type: 'system', systemId: payload._id };
        } else if (type === 'icon') {
            nodeClass = 'scada-icon';
            const IconComponent = getIconComponent(payload.iconName);
            const svgStr = renderToString(IconComponent);
            html = `
                <div class="scada-icon-wrapper" style="color: var(--primary);">
                    ${svgStr}
                </div>
            `;
            data = { type: 'icon', iconName: payload.iconName };
        } else if (type === 'shape') {
            nodeClass = 'scada-shape';
            const shapeType = payload.shapeType;
            let initW = 100, initH = 100;
            if (shapeType === 'line') { initW = 100; initH = 4; }
            else if (shapeType === 'v-line') { initW = 4; initH = 100; }

            html = `
                <div class="scada-shape-node resizable-shape" style="width: ${initW}px; height: ${initH}px; border-radius: ${shapeType === 'circle' ? '50%' : '0'}; background: ${shapeType === 'line' || shapeType === 'v-line' ? 'var(--primary)' : 'rgba(96, 165, 250, 0.1)'}; border: ${shapeType === 'line' || shapeType === 'v-line' ? 'none' : '2px solid var(--primary)'}; display: flex; align-items: center; justify-content: center; resize: both; overflow: hidden;">
                </div>
            `;
            inputs = 1; outputs = 1;
            data = { type: 'shape', shapeType, width: initW, height: initH };
        } else if (type === 'text') {
            nodeClass = 'scada-text';
            html = `
                <div class="scada-text-node">
                    <textarea df-text placeholder="${t('scada.placeholderText')}" style="background: transparent; border: none; color: var(--text-primary); font-family: inherit; font-size: 16px; font-weight: 600; resize: both; outline: none; padding: 4px;"></textarea>
                </div>
            `;
            inputs = 1; outputs = 1;
            data = { type: 'text', text: '' };
        } else if (type === 'status-signal') {
            nodeClass = 'scada-status-signal';
            html = `
                <div class="scada-status-node" style="display: flex; flex-direction: column; align-items: center; justify-content: center; width: 100px; height: 100px;">
                    <div class="signal-indicator" data-signal-id="${payload._id}" style="width: 50px; height: 50px; border-radius: 50%; background-color: var(--text-muted); display: flex; align-items: center; justify-content: center; box-shadow: 0 0 10px rgba(0,0,0,0.5); transition: background-color 0.3s; border: 3px solid rgba(255,255,255,0.1);">
                        <span style="color: white; font-size: 12px; font-weight: bold; pointer-events: none;">--</span>
                    </div>
                    <div style="text-align: center; margin-top: 8px; font-size: 12px; font-weight: bold; color: var(--text-primary);">
                        ${payload.name}
                    </div>
                </div>
            `;
            inputs = 1; outputs = 1;
            data = { type: 'status-signal', signalId: payload._id, name: payload.name };
        } else if (type === 'plc-output') {
            nodeClass = 'scada-plc-output';
            html = `
                <div class="scada-plc-outp">
                    <div class="scada-plc-node-label">${payload.name}</div>
                    <button class="scada-output-toggle" data-plc-id="${payload.plcId}" data-output-id="${payload.outputId}">
                        <div class="scada-toggle-track"><div class="scada-toggle-thumb"></div></div>
                        <span class="scada-toggle-label">KAPALI</span>
                    </button>
                </div>
            `;
            inputs = 1; outputs = 1;
            data = { type: 'plc-output', plcId: payload.plcId, outputId: payload.outputId, name: payload.name, plcName: payload.plcName };
        } else if (type === 'plc-refvalue') {
            nodeClass = 'scada-plc-refvalue';
            html = `
                <div class="scada-plc-outp">
                    <div class="scada-plc-node-label">${payload.name}</div>
                    <div class="scada-ref-row">
                        <input class="scada-ref-input" data-plc-id="${payload.plcId}" data-ref-id="${payload.refId}" value="${payload.value}" type="text" />
                        <button class="scada-ref-save" data-plc-id="${payload.plcId}" data-ref-id="${payload.refId}">Kaydet</button>
                    </div>
                </div>
            `;
            inputs = 1; outputs = 1;
            data = { type: 'plc-refvalue', plcId: payload.plcId, refId: payload.refId, name: payload.name, plcName: payload.plcName, value: payload.value };
        }

        drawflowInstance.current.addNode(payload.name || type, inputs, outputs, finalX, finalY, nodeClass, data, html);
    };

    const getIconComponent = (name) => {
        const props = { size: 40 };
        switch (name) {
            // react-icons/fa
            case 'FaHome': return <FaHome {...props} />;
            case 'FaWater': return <FaWater {...props} />;
            case 'FaFan': return <FaFan {...props} />;
            case 'FaBuilding': return <FaBuilding {...props} />;
            case 'FaThermometerHalf': return <FaThermometerHalf {...props} />;
            case 'FaNetworkWired': return <FaNetworkWired {...props} />;
            case 'FaTools': return <FaTools {...props} />;
            case 'FaTint': return <FaTint {...props} />;
            case 'FaFilter': return <FaFilter {...props} />;
            case 'FaIndustry': return <FaIndustry {...props} />;
            case 'FaLeaf': return <FaLeaf {...props} />;
            case 'FaFlask': return <FaFlask {...props} />;
            case 'FaExclamationTriangle': return <FaExclamationTriangle {...props} />;
            case 'FaBolt': return <FaBolt {...props} />;
            case 'FaDatabase': return <FaDatabase {...props} />;
            case 'FaServer': return <FaServer {...props} />;
            case 'FaCogs': return <FaCogs {...props} />;
            case 'FaWrench': return <FaWrench {...props} />;
            case 'FaGasPump': return <FaGasPump {...props} />;
            // react-icons/gi (only verified-existing icons)
            case 'GiWaterTower': return <GiWaterTower {...props} />;
            case 'GiValve': return <GiValve {...props} />;
            case 'GiPipes': return <GiPipes {...props} />;
            case 'GiOilDrum': return <GiOilDrum {...props} />;
            case 'GiFactory': return <GiFactory {...props} />;
            case 'GiWaterfall': return <GiWaterfall {...props} />;
            case 'GiPressureCooker': return <GiPressureCooker {...props} />;
            case 'GiElectric': return <GiElectric {...props} />;
            case 'GiRadarSweep': return <GiRadarSweep {...props} />;
            case 'GiWaterSplash': return <GiWaterSplash {...props} />;
            case 'GiMechanicalArm': return <GiMechanicalArm {...props} />;
            case 'GiWaterDrop': return <GiWaterDrop {...props} />;
            case 'GiWaterRecycling': return <GiWaterRecycling {...props} />;
            case 'GiChemicalTank': return <GiChemicalTank {...props} />;
            case 'GiChemicalArrow': return <GiChemicalArrow {...props} />;
            case 'GiChemicalBolt': return <GiChemicalBolt {...props} />;
            case 'GiVial': return <GiVial {...props} />;
            case 'GiWaterMill': return <GiWaterMill {...props} />;
            case 'GiPipeOrgan': return <GiPipeOrgan {...props} />;
            case 'GiTank': return <GiTank {...props} />;
            case 'GiLightningStorm': return <GiLightningStorm {...props} />;
            case 'GiGears': return <GiGears {...props} />;
            case 'GiWrench': return <GiWrench {...props} />;
            case 'GiSpanner': return <GiSpanner {...props} />;
            case 'GiPowerGenerator': return <GiPowerGenerator {...props} />;
            case 'GiOilPump': return <GiOilPump {...props} />;
            case 'GiBlender': return <GiBlender {...props} />;
            case 'GiMolecule': return <GiMolecule {...props} />;
            case 'GiAcid': return <GiAcid {...props} />;
            // react-icons/tb (only verified-existing icons)
            case 'TbDroplet': return <TbDroplet {...props} />;
            case 'TbDropletHalf2': return <TbDropletHalf2 {...props} />;
            case 'TbWaveSine': return <TbWaveSine {...props} />;
            case 'TbTemperature': return <TbTemperature {...props} />;
            case 'TbGauge': return <TbGauge {...props} />;
            case 'TbEngine': return <TbEngine {...props} />;
            case 'TbPropeller': return <TbPropeller {...props} />;
            case 'TbAirConditioning': return <TbAirConditioning {...props} />;
            case 'TbBuildingFactory2': return <TbBuildingFactory2 {...props} />;
            case 'TbChartLine': return <TbChartLine {...props} />;
            case 'TbAlertTriangle': return <TbAlertTriangle {...props} />;
            case 'TbPlugConnected': return <TbPlugConnected {...props} />;
            case 'TbBattery2': return <TbBattery2 {...props} />;
            case 'TbSunElectricity': return <TbSunElectricity {...props} />;
            default: return <FaCircle {...props} />;
        }
    };

    if (loading) return <div className="loading-page"><div className="spinner"></div></div>;

    const allVisibleOutputs = plcCards.flatMap(plc =>
        (plc.outputs || []).filter(o => o.companyVisible).map(o => ({ output: o, plc }))
    );
    const allVisibleRefValues = plcCards.flatMap(plc =>
        (plc.referenceValues || []).filter(r => r.companyVisible).map(r => ({ ref: r, plc }))
    );

    const ICON_GROUPS = [
        {
            label: t('scada.groupWater'),
            icons: [
                { id: 'FaWater',          name: t('scada.iconWater'),            comp: <FaWater size={22} /> },
                { id: 'FaTint',           name: t('scada.iconDrop'),             comp: <FaTint size={22} /> },
                { id: 'GiWaterDrop',      name: t('scada.iconWaterDrop'),        comp: <GiWaterDrop size={22} /> },
                { id: 'TbDroplet',        name: t('scada.iconDroplet'),          comp: <TbDroplet size={22} /> },
                { id: 'TbDropletHalf2',   name: t('scada.iconHalfFill'),         comp: <TbDropletHalf2 size={22} /> },
                { id: 'GiWaterTower',     name: t('scada.iconWaterTower'),       comp: <GiWaterTower size={22} /> },
                { id: 'GiWaterMill',      name: t('scada.iconWaterMill'),        comp: <GiWaterMill size={22} /> },
                { id: 'GiWaterRecycling', name: t('scada.iconRecycling'),        comp: <GiWaterRecycling size={22} /> },
                { id: 'FaFilter',         name: t('scada.iconFilter'),           comp: <FaFilter size={22} /> },
                { id: 'GiOilDrum',        name: t('scada.iconTank'),             comp: <GiOilDrum size={22} /> },
            ]
        },
        {
            label: t('scada.groupChemical'),
            icons: [
                { id: 'FaFlask',          name: t('scada.iconChemical'),         comp: <FaFlask size={22} /> },
                { id: 'GiChemicalTank',   name: t('scada.iconChemicalTank'),     comp: <GiChemicalTank size={22} /> },
                { id: 'GiChemicalBolt',   name: t('scada.iconChemicalEnergy'),   comp: <GiChemicalBolt size={22} /> },
                { id: 'GiVial',           name: t('scada.iconTube'),             comp: <GiVial size={22} /> },
                { id: 'GiMolecule',       name: t('scada.iconMolecule'),         comp: <GiMolecule size={22} /> },
                { id: 'GiAcid',           name: t('scada.iconAcid'),             comp: <GiAcid size={22} /> },
                { id: 'FaLeaf',           name: t('scada.iconEnvironment'),      comp: <FaLeaf size={22} /> },
            ]
        },
        {
            label: t('scada.groupEquipment'),
            icons: [
                { id: 'GiValve',          name: t('scada.iconValve'),            comp: <GiValve size={22} /> },
                { id: 'GiPipes',          name: t('scada.iconPipe'),             comp: <GiPipes size={22} /> },
                { id: 'GiMechanicalArm',  name: t('scada.iconMechArm'),          comp: <GiMechanicalArm size={22} /> },
                { id: 'FaFan',            name: t('scada.iconFan'),              comp: <FaFan size={22} /> },
                { id: 'TbPropeller',      name: t('scada.iconPropeller'),        comp: <TbPropeller size={22} /> },
                { id: 'TbEngine',         name: t('scada.iconEngine'),           comp: <TbEngine size={22} /> },
                { id: 'GiGears',          name: t('scada.iconGears'),            comp: <GiGears size={22} /> },
                { id: 'GiWrench',         name: t('scada.iconWrench'),           comp: <GiWrench size={22} /> },
                { id: 'GiSpanner',        name: t('scada.iconNutKey'),           comp: <GiSpanner size={22} /> },
                { id: 'FaTools',          name: t('scada.iconTools'),            comp: <FaTools size={22} /> },
            ]
        },
        {
            label: t('scada.groupEnergy'),
            icons: [
                { id: 'FaBolt',           name: t('scada.iconElectricity'),      comp: <FaBolt size={22} /> },
                { id: 'GiPowerGenerator', name: t('scada.iconGenerator'),        comp: <GiPowerGenerator size={22} /> },
                { id: 'TbSunElectricity', name: t('scada.iconSolar'),            comp: <TbSunElectricity size={22} /> },
                { id: 'TbBattery2',       name: t('scada.iconBattery'),          comp: <TbBattery2 size={22} /> },
                { id: 'TbPlugConnected',  name: t('scada.iconConnection'),       comp: <TbPlugConnected size={22} /> },
                { id: 'FaGasPump',        name: t('scada.iconFuel'),             comp: <FaGasPump size={22} /> },
            ]
        },
        {
            label: t('scada.groupSensor'),
            icons: [
                { id: 'FaThermometerHalf', name: t('scada.iconTemperature'),     comp: <FaThermometerHalf size={22} /> },
                { id: 'TbTemperature',    name: t('scada.iconThermometer'),      comp: <TbTemperature size={22} /> },
                { id: 'TbGauge',          name: t('scada.iconManometer'),        comp: <TbGauge size={22} /> },
                { id: 'TbWaveSine',       name: t('scada.iconSignal'),           comp: <TbWaveSine size={22} /> },
                { id: 'GiRadarSweep',     name: t('scada.iconRadar'),            comp: <GiRadarSweep size={22} /> },
                { id: 'TbChartLine',      name: t('scada.iconChart'),            comp: <TbChartLine size={22} /> },
                { id: 'TbAlertTriangle',  name: t('scada.iconWarning'),          comp: <TbAlertTriangle size={22} /> },
                { id: 'FaExclamationTriangle', name: t('scada.iconAlarm'),       comp: <FaExclamationTriangle size={22} /> },
                { id: 'FaNetworkWired',   name: t('scada.iconNetwork'),          comp: <FaNetworkWired size={22} /> },
            ]
        },
        {
            label: t('scada.groupBuilding'),
            icons: [
                { id: 'FaBuilding',       name: t('scada.iconBuilding'),         comp: <FaBuilding size={22} /> },
                { id: 'FaIndustry',       name: t('scada.iconFactory'),          comp: <FaIndustry size={22} /> },
                { id: 'GiFactory',        name: t('scada.iconFacility'),         comp: <GiFactory size={22} /> },
                { id: 'TbBuildingFactory2', name: t('scada.iconProdFacility'),   comp: <TbBuildingFactory2 size={22} /> },
                { id: 'FaHome',           name: t('scada.iconHome'),             comp: <FaHome size={22} /> },
                { id: 'TbAirConditioning', name: t('scada.iconAc'),              comp: <TbAirConditioning size={22} /> },
                { id: 'FaCogs',           name: t('scada.iconSystem'),           comp: <FaCogs size={22} /> },
            ]
        },
    ];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, padding: '16px 24px', background: 'var(--bg-card)', borderBottom: '1px solid var(--border)', zIndex: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, minWidth: 0 }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => navigate(-1)}>
                        <MdArrowBack /> {t('common.back')}
                    </button>
                    <h2 style={{ margin: 0, fontSize: 20, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t('scada.title', { name: company?.name })}</h2>
                </div>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => drawflowInstance.current.zoom_in()}>
                        <MdZoomIn />
                    </button>
                    <button className="btn btn-secondary btn-sm" onClick={() => drawflowInstance.current.zoom_out()}>
                        <MdZoomOut />
                    </button>
                    <button className="btn btn-warning btn-sm" onClick={() => {
                        if (window.confirm(t('scada.clearConfirm'))) {
                            drawflowInstance.current.clear();
                        }
                    }}>
                        {t('scada.clearBtn')}
                    </button>
                    <button className="btn btn-primary btn-sm" onClick={saveLayout}>
                        <MdSave /> {t('scada.saveBtn')}
                    </button>
                </div>
            </div>

            {/* Main Area */}
            <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
                {/* Sidebar Toolbox */}
                <div style={{ width: 280, background: 'var(--bg-card)', borderRight: '1px solid var(--border)', overflowY: 'auto', padding: 16 }}>
                    <h4 style={{ fontSize: 13, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 12 }}>{t('scada.systems')}</h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
                        {company?.systems?.map(sys => (
                            <div
                                key={sys._id}
                                className="draggable-item"
                                draggable
                                onDragStart={(e) => onDragStart(e, 'system', sys)}
                                style={{
                                    padding: '10px 14px', background: 'var(--bg-card)', border: '1px solid var(--border)',
                                    borderRadius: 6, cursor: 'grab', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600
                                }}
                            >
                                <FaNetworkWired style={{ color: 'var(--primary)' }} />
                                {sys.systemName}
                            </div>
                        ))}
                        {(!company?.systems || company.systems.length === 0) && (
                            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{t('scada.noSystems')}</div>
                        )}
                    </div>

                    <h4 style={{ fontSize: 13, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 12 }}>{t('scada.statusSignals')}</h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
                        {company?.statusSignals?.map(sig => (
                            <div
                                key={sig._id}
                                className="draggable-item"
                                draggable
                                onDragStart={(e) => onDragStart(e, 'status-signal', sig)}
                                style={{
                                    padding: '10px 14px', background: 'var(--bg-card)', border: '1px solid var(--border)',
                                    borderRadius: 6, cursor: 'grab', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600
                                }}
                            >
                                <div style={{ width: 12, height: 12, borderRadius: '50%', background: 'var(--primary)' }}></div>
                                {sig.name}
                            </div>
                        ))}
                        {(!company?.statusSignals || company.statusSignals.length === 0) && (
                            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{t('scada.noSignals')}</div>
                        )}
                    </div>

                    <h4 style={{ fontSize: 13, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 12 }}>{t('scada.outputs')}</h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
                        {allVisibleOutputs.map(({ output, plc }) => (
                            <div
                                key={output._id}
                                className="draggable-item"
                                draggable
                                onDragStart={(e) => onDragStart(e, 'plc-output', {
                                    plcId: plc._id,
                                    plcName: plc.plcName,
                                    outputId: output._id,
                                    name: output.name,
                                    state: output.state
                                })}
                                style={{
                                    padding: '10px 14px', background: 'var(--bg-card)', border: '1px solid var(--border)',
                                    borderRadius: 6, cursor: 'grab', display: 'flex', alignItems: 'center', gap: 10, fontSize: 13
                                }}
                            >
                                <div style={{
                                    width: 28, height: 28, borderRadius: 6, flexShrink: 0,
                                    background: 'rgba(251,191,36,0.15)', border: '1px solid rgba(251,191,36,0.35)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                                }}>
                                    <span style={{ fontSize: 13 }}>⚡</span>
                                </div>
                                <div style={{ minWidth: 0 }}>
                                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{output.name}</div>
                                    <div style={{ fontSize: 10, color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{plc.plcName}</div>
                                </div>
                            </div>
                        ))}
                        {allVisibleOutputs.length === 0 && (
                            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{t('scada.noOutputs')}</div>
                        )}
                    </div>

                    <h4 style={{ fontSize: 13, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 12 }}>{t('scada.refValues')}</h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
                        {allVisibleRefValues.map(({ ref, plc }) => (
                            <div
                                key={ref._id}
                                className="draggable-item"
                                draggable
                                onDragStart={(e) => onDragStart(e, 'plc-refvalue', {
                                    plcId: plc._id,
                                    plcName: plc.plcName,
                                    refId: ref._id,
                                    name: ref.name,
                                    value: ref.value
                                })}
                                style={{
                                    padding: '10px 14px', background: 'var(--bg-card)', border: '1px solid var(--border)',
                                    borderRadius: 6, cursor: 'grab', display: 'flex', alignItems: 'center', gap: 10, fontSize: 13
                                }}
                            >
                                <div style={{
                                    width: 28, height: 28, borderRadius: 6, flexShrink: 0,
                                    background: 'rgba(79,142,247,0.15)', border: '1px solid rgba(79,142,247,0.3)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                                }}>
                                    <span style={{ fontSize: 13 }}>⚙</span>
                                </div>
                                <div style={{ minWidth: 0 }}>
                                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ref.name}</div>
                                    <div style={{ fontSize: 10, color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{plc.plcName} — {ref.value}</div>
                                </div>
                            </div>
                        ))}
                        {allVisibleRefValues.length === 0 && (
                            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{t('scada.noRefValues')}</div>
                        )}
                    </div>

                    <h4 style={{ fontSize: 13, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 12 }}>{t('scada.icons')}</h4>
                    {ICON_GROUPS.map(group => (
                        <div key={group.label} style={{ marginBottom: 20 }}>
                            <div style={{
                                fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                                letterSpacing: '0.08em', color: 'var(--primary)',
                                marginBottom: 8, paddingBottom: 4,
                                borderBottom: '1px solid rgba(79,142,247,0.2)'
                            }}>{group.label}</div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
                                {group.icons.map(icon => (
                                    <div
                                        key={icon.id}
                                        className="draggable-item"
                                        draggable
                                        onDragStart={(e) => onDragStart(e, 'icon', { iconName: icon.id })}
                                        title={icon.name}
                                        style={{
                                            padding: '8px 4px', background: 'var(--bg-elevated)',
                                            border: '1px solid var(--border)', borderRadius: 8,
                                            cursor: 'grab', display: 'flex', flexDirection: 'column',
                                            alignItems: 'center', gap: 5, fontSize: 10,
                                            textAlign: 'center', userSelect: 'none',
                                            transition: 'border-color 0.15s, background 0.15s'
                                        }}
                                        onMouseEnter={e => {
                                            e.currentTarget.style.borderColor = 'var(--primary)';
                                            e.currentTarget.style.background = 'rgba(79,142,247,0.08)';
                                        }}
                                        onMouseLeave={e => {
                                            e.currentTarget.style.borderColor = 'var(--border)';
                                            e.currentTarget.style.background = 'var(--bg-elevated)';
                                        }}
                                    >
                                        <div style={{ color: 'var(--primary)' }}>{icon.comp}</div>
                                        <span style={{ color: 'var(--text-muted)', lineHeight: 1.2 }}>{icon.name}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}

                    <h4 style={{ fontSize: 13, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 12, marginTop: 24 }}>{t('scada.shapesAndLines')}</h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
                        <div
                            className="draggable-item"
                            draggable
                            onDragStart={(e) => onDragStart(e, 'shape', { shapeType: 'square' })}
                            style={{
                                padding: '10px 14px', background: 'var(--bg-card)', border: '1px solid var(--border)',
                                borderRadius: 6, cursor: 'grab', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13
                            }}
                        >
                            <MdCropSquare size={20} style={{ color: 'var(--primary)' }} /> {t('scada.square')}
                        </div>
                        <div
                            className="draggable-item"
                            draggable
                            onDragStart={(e) => onDragStart(e, 'shape', { shapeType: 'circle' })}
                            style={{
                                padding: '10px 14px', background: 'var(--bg-card)', border: '1px solid var(--border)',
                                borderRadius: 6, cursor: 'grab', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13
                            }}
                        >
                            <MdCircle size={20} style={{ color: 'var(--primary)' }} /> {t('scada.circle')}
                        </div>
                        <div
                            className="draggable-item"
                            draggable
                            onDragStart={(e) => onDragStart(e, 'shape', { shapeType: 'line' })}
                            style={{
                                padding: '10px 14px', background: 'var(--bg-card)', border: '1px solid var(--border)',
                                borderRadius: 6, cursor: 'grab', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13
                            }}
                        >
                            <div style={{ width: 20, height: 4, background: 'var(--primary)' }}></div> {t('scada.line')}
                        </div>
                        <div
                            className="draggable-item"
                            draggable
                            onDragStart={(e) => onDragStart(e, 'shape', { shapeType: 'v-line' })}
                            style={{
                                padding: '10px 14px', background: 'var(--bg-card)', border: '1px solid var(--border)',
                                borderRadius: 6, cursor: 'grab', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13
                            }}
                        >
                            <div style={{ width: 4, height: 20, background: 'var(--primary)', marginLeft: 8, marginRight: 6 }}></div> {t('scada.vLine')}
                        </div>
                        <div
                            className="draggable-item"
                            draggable
                            onDragStart={(e) => onDragStart(e, 'text', {})}
                            style={{
                                padding: '10px 14px', background: 'var(--bg-card)', border: '1px solid var(--border)',
                                borderRadius: 6, cursor: 'grab', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13
                            }}
                        >
                            <MdFormatColorText size={20} style={{ color: 'var(--primary)' }} /> {t('scada.text')}
                        </div>
                    </div>

                    <div style={{ marginTop: 32, padding: 12, background: 'rgba(79, 142, 247, 0.1)', borderRadius: 8, fontSize: 11, color: 'var(--text-secondary)' }}>
                        <strong>{t('common.detail')}:</strong> {t('scada.tooltipHelp')}
                    </div>
                </div>

                {/* Drawflow Canvas Area */}
                <div style={{
                    flex: 1,
                    position: 'relative',
                    backgroundColor: 'var(--bg-body)',
                    backgroundImage: 'repeating-linear-gradient(rgba(128,128,128,0.1) 0, rgba(128,128,128,0.1) 1px, transparent 1px, transparent 40px), repeating-linear-gradient(90deg, rgba(128,128,128,0.1) 0, rgba(128,128,128,0.1) 1px, transparent 1px, transparent 40px)'
                }}
                    onDrop={onDrop}
                    onDragOver={(e) => e.preventDefault()}
                >
                    <div ref={editorRef} id="drawflow" style={{ width: '100%', height: '100%' }}></div>
                </div>
            </div>

            {/* Drawflow Custom Global CSS Overrides */}
            <style dangerouslySetInnerHTML={{
                __html: `
                .drawflow {
                    background: transparent;
                }
                .drawflow .connection .main-path {
                    stroke: var(--primary) !important;
                    stroke-width: 5px !important;
                    stroke-dasharray: 10 10 !important;
                    animation: dashFlow 1s linear infinite !important;
                    filter: drop-shadow(0 0 4px var(--primary)) !important;
                }
                .drawflow .connection .point {
                    stroke: var(--primary) !important;
                    stroke-width: 2px !important;
                    fill: var(--bg-card) !important;
                }
                @keyframes dashFlow {
                    from { stroke-dashoffset: 20; }
                    to { stroke-dashoffset: 0; }
                }

                .drawflow-node {
                    background: transparent !important;
                    border: none !important;
                    border-radius: 0 !important;
                    color: var(--text-primary) !important;
                    box-shadow: none !important;
                    padding: 0 !important;
                    width: auto !important;
                }
                
                /* Sistem Node'u için Özel Stil (Dikdörtgen kutuyu geri getirir) */
                .drawflow-node.scada-system {
                    background: var(--bg-card) !important;
                    border: 1px solid var(--border) !important;
                    border-radius: 12px !important;
                    box-shadow: 0 4px 15px rgba(0,0,0,0.15) !important;
                    min-width: 260px !important;
                }
                
                /* İkon Node'u */
                .drawflow-node.scada-icon {
                    background: transparent !important;
                }

                /* Şekiller */
                .drawflow-node.scada-shape {
                    background: transparent !important;
                }
                
                /* Kutusuz Metin */
                .drawflow-node.scada-text {
                    background: transparent !important;
                }
                .drawflow-node.scada-text.selected {
                    border: 1px dashed var(--primary) !important; /* sadece seçiliyken hafif belli olsun */
                }

                /* Durum Sinyali Node'u */
                .drawflow-node.scada-status-signal {
                    background: transparent !important;
                }

                .drawflow_content_node {
                    padding: 0 !important;
                    margin: 0 !important;
                    display: block;
                    width: 100% !important;
                    box-sizing: border-box !important;
                }
                .drawflow-node.selected {
                    border: 2px solid var(--primary) !important;
                }
                .drawflow-node .inputs, .drawflow-node .outputs {
                    margin-top: 5px;
                }
                .drawflow-node .input, .drawflow-node .output {
                    width: 10px !important;
                    height: 3px !important;
                    border-radius: 0 !important;
                    background: var(--bg-body) !important;
                    border: 2px solid var(--primary) !important; /* Admin için kalın belirgin çerçeve */
                }
                .drawflow-node .input {
                    left: -10px !important; /* admin tarafı çıkıntı oranı */
                }
                .drawflow-node .output {
                    right: 0px !important;
                }
                .drawflow-node .input:hover, .drawflow-node .output:hover {
                    background: var(--primary) !important;
                }

                .scada-node-card { padding: 0; min-width: 100%; box-sizing: border-box; background: transparent; }
                .scada-node-header {
                    background: rgba(79, 142, 247, 0.1) !important;
                    padding: 12px 16px !important;
                    border-bottom: 1px solid var(--border) !important;
                    border-radius: 12px 12px 0 0 !important;
                    font-weight: 700;
                    font-size: 15px;
                    color: var(--primary) !important;
                    display: flex; gap: 8px; align-items: center;
                    width: 100%;
                    box-sizing: border-box;
                }
                .scada-node-body {
                    padding: 12px !important;
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                    background: transparent !important;
                    width: 100%;
                    box-sizing: border-box;
                }
                .scada-sensor-item {
                    display: flex; justify-content: space-between; align-items: center;
                    background: var(--bg-body) !important;
                    padding: 8px 12px;
                    border-radius: 6px;
                    font-size: 12px;
                    cursor: pointer;
                    transition: all 0.2s;
                    border: 1px solid var(--border) !important;
                    color: var(--text-primary) !important;
                }
                .scada-sensor-item:hover {
                    background: var(--bg-card) !important;
                    border-color: var(--primary) !important;
                }
                .scada-sensor-item .sensor-name { font-size: 14px; font-weight: 600; }
                .scada-sensor-item .sensor-val { font-size: 18px; font-weight: 800; color: var(--success) !important; font-family: monospace; }
                
                .scada-icon-wrapper { padding: 10px; display: flex; align-items: center; justify-content: center; }
                .scada-text-node textarea {
                    min-height: 40px;
                    color: var(--text-primary) !important;
                }
                .scada-text-node textarea:focus { outline: none; border-color: var(--primary); }

                .drawflow-node.scada-plc-output {
                    background: var(--bg-card) !important;
                    border: 1px solid var(--border) !important;
                    border-radius: 10px !important;
                    min-width: 150px !important;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.15) !important;
                }
                .drawflow-node.scada-plc-refvalue {
                    background: var(--bg-card) !important;
                    border: 1px solid var(--border) !important;
                    border-radius: 10px !important;
                    min-width: 200px !important;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.15) !important;
                }
                .scada-plc-outp { padding: 12px; box-sizing: border-box; }
                .scada-plc-node-label {
                    font-size: 13px; font-weight: 600; color: var(--text-primary);
                    margin-bottom: 10px; text-align: center;
                }
                .scada-output-toggle {
                    display: flex; align-items: center; gap: 8px;
                    background: none; border: 1px solid var(--border); border-radius: 20px;
                    padding: 6px 10px; cursor: pointer; width: 100%; justify-content: center;
                    transition: border-color 0.2s;
                }
                .scada-output-toggle.is-on {
                    border-color: var(--success); background: rgba(34, 197, 94, 0.08);
                }
                .scada-toggle-track {
                    position: relative; width: 36px; height: 20px; border-radius: 10px;
                    background: var(--text-muted); flex-shrink: 0; transition: background 0.25s;
                }
                .scada-output-toggle.is-on .scada-toggle-track { background: var(--success); }
                .scada-toggle-thumb {
                    position: absolute; top: 3px; left: 3px; width: 14px; height: 14px;
                    border-radius: 50%; background: white; transition: left 0.25s;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.3);
                }
                .scada-output-toggle.is-on .scada-toggle-thumb { left: 19px; }
                .scada-toggle-label { font-size: 12px; font-weight: 700; color: var(--text-muted); }
                .scada-output-toggle.is-on .scada-toggle-label { color: var(--success); }
                .scada-ref-row { display: flex; align-items: center; gap: 6px; }
                .scada-ref-input {
                    flex: 1; background: var(--bg-body); border: 1px solid var(--border);
                    border-radius: 6px; color: var(--primary); font-family: monospace;
                    font-size: 13px; font-weight: 700; padding: 5px 8px; text-align: right;
                    min-width: 0; box-sizing: border-box;
                }
                .scada-ref-save {
                    flex-shrink: 0; background: var(--primary); border: none; border-radius: 6px;
                    color: white; font-size: 13px; font-weight: 700; padding: 5px 10px; cursor: pointer;
                }
            `}} />
        </div>
    );
}
